import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { toast } from 'sonner';
import { Mountain, Mail, Lock, User, Building, MapPin, FileText, Calendar, Eye, EyeOff } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const LOGO_URL = "https://customer-assets.emergentagent.com/job_comunelaval-halls/artifacts/khvga6ci_Cuns%C3%ABi%20de%20Comun%2020251017_124442.jpg";

export default function Register() {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    nome_cognome: '',
    societa_associazione: '',
    indirizzo: '',
    casella_postale: '',
    comune_residenza: '',
    codice_fiscale: '',
    partita_iva: '',
    tipo_richiedente: '',
    tipo_soggetto: '',
    codice_fattura_digitale: ''
  });
  const [loading, setLoading] = useState(false);
  const [halls, setHalls] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API_URL}/halls`).then(res => setHalls(res.data)).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Validate required fields
    if (!formData.email || !formData.password || !formData.nome_cognome || !formData.tipo_soggetto || !formData.tipo_richiedente) {
      toast.error('Compila tutti i campi obbligatori contrassegnati con *');
      return;
    }
    if (formData.password.length < 6) {
      toast.error('La password deve avere almeno 6 caratteri');
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      toast.error('Le password non corrispondono');
      return;
    }
    setLoading(true);
    try {
      const { confirmPassword, ...submitData } = formData;
      await register(submitData);
      toast.success('Registrazione completata!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante la registrazione');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="flex items-center justify-center p-8 lg:p-12">
        <div className="w-full max-w-md space-y-8">
          <div className="text-center space-y-2">
            <div className="flex items-center justify-center gap-2 mb-6">
              <img src={LOGO_URL} alt="Comune di La Val" className="w-12 h-12 rounded-full object-cover" />
              <h1 className="font-heading font-extrabold text-3xl tracking-tight">
                <span className="font-bold">Salf</span>
                <span className="font-light"> manifestaziuns</span>
              </h1>
            </div>
            <h2 className="font-heading font-semibold text-2xl">Registrazione</h2>
            <p className="text-muted-foreground">Crea il tuo account</p>
          </div>

          <Card className="p-8">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-5 w-5 text-muted-foreground" />
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="tuo@email.com"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="pl-10"
                    data-testid="register-email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password *</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-5 w-5 text-muted-foreground" />
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    className="pl-10 pr-10"
                    data-testid="register-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-3 text-muted-foreground hover:text-foreground transition-colors"
                    data-testid="toggle-password-visibility"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Conferma Password *</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-5 w-5 text-muted-foreground" />
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    required
                    className="pl-10 pr-10"
                    data-testid="register-confirm-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-3 text-muted-foreground hover:text-foreground transition-colors"
                    data-testid="toggle-confirm-password-visibility"
                    tabIndex={-1}
                  >
                    {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
                {formData.confirmPassword && formData.password !== formData.confirmPassword && (
                  <p className="text-xs text-destructive" data-testid="password-mismatch-error">Le password non corrispondono</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="nome_cognome">Nome e Cognome *</Label>
                <div className="relative">
                  <User className="absolute left-3 top-3 h-5 w-5 text-muted-foreground" />
                  <Input
                    id="nome_cognome"
                    name="nome_cognome"
                    placeholder="Mario Rossi"
                    value={formData.nome_cognome}
                    onChange={handleChange}
                    required
                    className="pl-10"
                    data-testid="register-nome-input"
                  />
                </div>
              </div>

              {/* Tipo Soggetto */}
              <div className="space-y-2">
                <Label className="font-semibold">Tipo Soggetto *</Label>
                <RadioGroup
                  value={formData.tipo_soggetto}
                  onValueChange={(value) => setFormData({ ...formData, tipo_soggetto: value })}
                  className="flex flex-wrap gap-4"
                  data-testid="tipo-soggetto-group"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="persona_fisica" id="pf" data-testid="radio-persona-fisica" />
                    <Label htmlFor="pf" className="cursor-pointer text-sm">Persona fisica</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="azienda" id="az" data-testid="radio-azienda" />
                    <Label htmlFor="az" className="cursor-pointer text-sm">Azienda</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="associazione" id="as" data-testid="radio-associazione" />
                    <Label htmlFor="as" className="cursor-pointer text-sm">Associazione</Label>
                  </div>
                </RadioGroup>
              </div>

              {/* Codice Fattura Digitale - only for azienda */}
              {formData.tipo_soggetto === 'azienda' && (
                <div className="space-y-2">
                  <Label htmlFor="codice_fattura_digitale">Codice Fattura Digitale (SDI)</Label>
                  <div className="relative">
                    <FileText className="absolute left-3 top-3 h-5 w-5 text-muted-foreground" />
                    <Input
                      id="codice_fattura_digitale"
                      name="codice_fattura_digitale"
                      placeholder="Es. A1B2C3D"
                      value={formData.codice_fattura_digitale}
                      onChange={handleChange}
                      className="pl-10"
                      data-testid="register-codice-fattura-input"
                    />
                  </div>
                </div>
              )}

              {/* Tipo Richiedente */}
              <div className="space-y-2">
                <Label className="font-semibold">Tipo Richiedente *</Label>
                <RadioGroup
                  value={formData.tipo_richiedente}
                  onValueChange={(value) => setFormData({ ...formData, tipo_richiedente: value })}
                  className="space-y-2"
                  data-testid="tipo-richiedente-group"
                >
                  <div className="flex items-start space-x-2 p-2 rounded-lg hover:bg-muted">
                    <RadioGroupItem value="1" id="tr1" data-testid="radio-richiedente-1" className="mt-0.5" />
                    <Label htmlFor="tr1" className="cursor-pointer text-sm leading-snug">Associazioni di pubblica utilità residenti</Label>
                  </div>
                  <div className="flex items-start space-x-2 p-2 rounded-lg hover:bg-muted">
                    <RadioGroupItem value="2" id="tr2" data-testid="radio-richiedente-2" className="mt-0.5" />
                    <Label htmlFor="tr2" className="cursor-pointer text-sm leading-snug">Associazioni non residenti e persone residenti</Label>
                  </div>
                  <div className="flex items-start space-x-2 p-2 rounded-lg hover:bg-muted">
                    <RadioGroupItem value="3" id="tr3" data-testid="radio-richiedente-3" className="mt-0.5" />
                    <Label htmlFor="tr3" className="cursor-pointer text-sm leading-snug">Non residenti</Label>
                  </div>
                </RadioGroup>
              </div>

              <div className="space-y-2">
                <Label htmlFor="societa_associazione">Società o Associazione</Label>
                <div className="relative">
                  <Building className="absolute left-3 top-3 h-5 w-5 text-muted-foreground" />
                  <Input
                    id="societa_associazione"
                    name="societa_associazione"
                    placeholder="Nome società"
                    value={formData.societa_associazione}
                    onChange={handleChange}
                    className="pl-10"
                    data-testid="register-societa-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="indirizzo">Indirizzo</Label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-3 h-5 w-5 text-muted-foreground" />
                  <Input
                    id="indirizzo"
                    name="indirizzo"
                    placeholder="Via, Città"
                    value={formData.indirizzo}
                    onChange={handleChange}
                    className="pl-10"
                    data-testid="register-indirizzo-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="casella_postale">Casella Postale</Label>
                  <Input
                    id="casella_postale"
                    name="casella_postale"
                    placeholder="C.P. 123"
                    value={formData.casella_postale}
                    onChange={handleChange}
                    data-testid="register-casella-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="comune_residenza">Comune di Residenza</Label>
                  <Input
                    id="comune_residenza"
                    name="comune_residenza"
                    placeholder="La Val"
                    value={formData.comune_residenza}
                    onChange={handleChange}
                    data-testid="register-comune-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="codice_fiscale">Codice Fiscale</Label>
                  <Input
                    id="codice_fiscale"
                    name="codice_fiscale"
                    placeholder="RSSMRA80A01H501U"
                    value={formData.codice_fiscale}
                    onChange={handleChange}
                    data-testid="register-cf-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="partita_iva">Partita IVA</Label>
                  <Input
                    id="partita_iva"
                    name="partita_iva"
                    placeholder="12345678901"
                    value={formData.partita_iva}
                    onChange={handleChange}
                    data-testid="register-piva-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full btn-scale"
                disabled={loading || !formData.email || !formData.password || !formData.confirmPassword || !formData.nome_cognome || !formData.tipo_soggetto || !formData.tipo_richiedente || formData.password !== formData.confirmPassword}
                data-testid="register-submit-button"
              >
                {loading ? 'Registrazione...' : 'Registrati'}
              </Button>
            </form>
          </Card>

          <p className="text-center text-sm text-muted-foreground">
            Hai già un account?{' '}
            <Link to="/login" className="text-primary hover:underline font-medium" data-testid="login-link">
              Accedi qui
            </Link>
          </p>

          {/* Public calendar links */}
          {halls.length > 0 && (
            <div className="space-y-3 p-4 rounded-xl bg-green-50 dark:bg-green-900/20 border-2 border-green-500">
              <p className="text-center text-sm sm:text-base font-bold text-green-800 dark:text-green-200">
                Consulta i calendari senza registrarti
              </p>
              <div className="flex flex-col gap-2">
                {halls.map(hall => (
                  <Button
                    key={hall.id}
                    size="lg"
                    className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold text-sm sm:text-base py-3"
                    onClick={() => navigate(`/public/calendar/${hall.id}`)}
                    data-testid={`public-calendar-register-${hall.id}`}
                  >
                    <Calendar className="w-5 h-5 mr-2" />
                    {hall.name}
                  </Button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="hidden lg:block relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1763985357104-af320fc8153a"
          alt="La Val Dolomites"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 hero-gradient flex items-center justify-center p-12">
          <div className="text-center text-white space-y-4">
            <h2 className="font-heading font-bold text-4xl lg:text-5xl">
              Comune di La Val
            </h2>
            <p className="text-xl opacity-90">Sistema di prenotazione sale manifestazioni</p>
          </div>
        </div>
      </div>
    </div>
  );
}